import { useCallback, useEffect, useRef, useState } from 'react';

const TOOLTIP_HIDE_MS = 300;

const resolveAnchorElement = (anchor, anchorMode) => {
  if (anchorMode === 'trigger') {
    return anchor.closest('a, button') || anchor;
  }
  return anchor;
};

const useFixedTooltip = (resetDeps = [], options = {}) => {
  const { offsetX = 0, anchorMode = 'element' } = options;
  const containerRef = useRef(null);
  const anchorRef = useRef(null);
  const hideTimeoutRef = useRef(null);
  const [activeContent, setActiveContent] = useState(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

  const getAnchorPosition = useCallback((anchor) => {
    const trigger = resolveAnchorElement(anchor, anchorMode);
    const rect = trigger.getBoundingClientRect();
    return {
      top: rect.top + rect.height / 2,
      left: rect.right + offsetX,
    };
  }, [anchorMode, offsetX]);

  const updateTooltipPosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    setTooltipPos(getAnchorPosition(anchor));
  }, [getAnchorPosition]);

  const showTooltip = useCallback((anchor, content) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    const isSameAnchor = anchorRef.current === anchor;
    anchorRef.current = anchor;
    setActiveContent(content);
    setTooltipPos(getAnchorPosition(anchor));

    if (isSameAnchor) {
      setTooltipVisible(true);
      return;
    }

    setTooltipVisible(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setTooltipVisible(true));
    });
  }, [getAnchorPosition]);

  const hideTooltip = useCallback(() => {
    setTooltipVisible(false);
    hideTimeoutRef.current = setTimeout(() => {
      anchorRef.current = null;
      setActiveContent(null);
      hideTimeoutRef.current = null;
    }, TOOLTIP_HIDE_MS);
  }, []);

  useEffect(() => {
    if (!activeContent) return undefined;
    const scrollContainer = containerRef.current;
    if (!scrollContainer) return undefined;

    const handleReposition = () => updateTooltipPosition();
    scrollContainer.addEventListener('scroll', handleReposition, { passive: true });
    window.addEventListener('resize', handleReposition, { passive: true });

    return () => {
      scrollContainer.removeEventListener('scroll', handleReposition);
      window.removeEventListener('resize', handleReposition);
    };
  }, [activeContent, updateTooltipPosition]);

  useEffect(() => {
    hideTooltip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetDeps);

  useEffect(
    () => () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    },
    [],
  );

  return {
    containerRef,
    showTooltip,
    hideTooltip,
    activeContent,
    tooltipVisible,
    tooltipPos,
  };
};

export default useFixedTooltip;
